import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DarkModeToggle } from '../shared/DarkModeToggle';
import { useToast } from '../shared/ToastNotification';

interface PortalLayoutProps {
    children: React.ReactNode;
    role: 'customer' | 'staff';
}

const PortalLayout: React.FC<PortalLayoutProps> = ({ children, role }) => {
    const location = useLocation();
    const { success } = useToast();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('user_role');
        success('Logged out successfully');
        window.location.href = '/login';
    };

    const customerLinks = [
        { path: '/portal', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
        { path: '/portal/orders', label: 'My Orders', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
        { path: '/portal/invoices', label: 'Invoices', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { path: '/portal/deliveries', label: 'Deliveries', icon: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0' },
        { path: '/store', label: 'Shop', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' },
    ];

    const staffLinks = [
        { path: '/staff', label: 'Staff Hub', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { path: '/staff/deliveries', label: 'Today\'s Work', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
        { path: '/products', label: 'Product Catalog', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
        { path: '/', label: 'ERP Dashboard', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    ];

    const links = role === 'customer' ? customerLinks : staffLinks;

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Top Navigation */}
            <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <Link to={role === 'customer' ? '/portal' : '/staff'} className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-black">
                                    {role === 'customer' ? 'CP' : 'SP'}
                                </div>
                                <span className="text-lg font-black text-slate-900 tracking-tight">
                                    {role === 'customer' ? 'Customer Portal' : 'Staff Portal'}
                                </span>
                            </Link>
                        </div>

                        {/* Desktop Links */}
                        <div className="hidden md:flex items-center gap-6">
                            {links.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`px-3 py-2 text-sm font-bold transition-colors ${
                                        location.pathname === link.path 
                                        ? 'text-indigo-600' 
                                        : 'text-slate-500 hover:text-slate-900'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <div className="h-6 w-[1px] bg-slate-200"></div>
                            <DarkModeToggle />
                            <button
                                onClick={handleLogout}
                                className="text-sm font-bold text-rose-500 hover:text-rose-700 transition-colors"
                            >
                                Sign Out
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden flex items-center gap-4">
                            <DarkModeToggle />
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden bg-white border-b border-slate-200 animate-in slide-in-from-top duration-200">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            {links.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-3 py-3 rounded-xl text-base font-bold transition-colors ${
                                        location.pathname === link.path 
                                        ? 'bg-indigo-50 text-indigo-600' 
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <button
                                onClick={handleLogout}
                                className="w-full text-left px-3 py-3 rounded-xl text-base font-bold text-rose-500 hover:bg-rose-50 transition-colors"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                )}
            </nav>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                        Hello, <span className="text-indigo-600">{user.name}</span>
                    </h1>
                    <p className="text-slate-500 font-medium">
                        {role === 'customer' ? 'Manage your orders and deliveries here.' : 'Field operations center.'}
                    </p>
                </div>
                {children}
            </main>

            {/* Mobile Bottom Navigation (Floating style) */}
            <div className="md:hidden fixed bottom-4 left-4 right-4 z-50">
                <div className="bg-white/90 backdrop-blur-lg border border-slate-200 rounded-2xl shadow-2xl px-6 py-3 flex justify-between items-center text-slate-400">
                    {links.map((link) => {
                        const active = location.pathname === link.path;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`flex flex-col items-center gap-1 transition-colors ${active ? 'text-indigo-600' : 'hover:text-slate-600'}`}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={link.icon} />
                                </svg>
                                <span className="text-[10px] font-black uppercase tracking-tight">{link.label.split(' ')[0]}</span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PortalLayout;
